// Bidirectional-text helpers for story text that mixes scripts — an Arabic /
// Hebrew (RTL) story with an embedded Latin name, or an English (LTR) story
// with an embedded Arabic/Hebrew word.
//
// The book page sets a BASE direction. The bidi algorithm already places a lone
// opposite-script word correctly; what it gets wrong is the BOUNDARY, where
// neutral characters (punctuation, brackets, digits) glued to that word attach
// to the wrong side and mirror/jump — "(John)", 'John!', "John Smith Jr.". We
// fix it by wrapping each MINORITY-direction run (plus its glued neutrals) in
// Unicode isolate marks so it is pinned to its own direction in place.
//
// The marks are invisible and add no DOM nodes, so the same output renders
// identically in the browser viewer and the headless-Chrome PDF.
// Keep this file in sync with server/services/bidi.js.

const LRI = '⁦'; // U+2066 LEFT-TO-RIGHT ISOLATE
const RLI = '⁧'; // U+2067 RIGHT-TO-LEFT ISOLATE
const PDI = '⁩'; // U+2069 POP DIRECTIONAL ISOLATE

// Latin letters (basic + Latin-1/extended). A run starts and ends on a letter
// or digit, keeping in-word spaces/punctuation between them.
const LTR_RUN = /[A-Za-zÀ-ɏ][A-Za-z0-9À-ɏ .,'’&()/-]*[A-Za-z0-9À-ɏ]|[A-Za-zÀ-ɏ]/g;

// Hebrew + Arabic letter blocks (incl. presentation forms). Same run shape.
const RTL_CH = '\\u0590-\\u05FF\\u0600-\\u06FF\\u0750-\\u077F\\u08A0-\\u08FF\\uFB1D-\\uFDFF\\uFE70-\\uFEFF';
const RTL_RUN = new RegExp(`[${RTL_CH}][${RTL_CH} .,'’&()/-]*[${RTL_CH}]|[${RTL_CH}]`, 'g');

export function isolateLtrRuns(str) {
  if (str == null || str === '') return str;
  return String(str).replace(LTR_RUN, (m) => LRI + m + PDI);
}

export function isolateRtlRuns(str) {
  if (str == null || str === '') return str;
  return String(str).replace(RTL_RUN, (m) => RLI + m + PDI);
}

// Isolate the runs that oppose the given BASE direction: in an RTL story pin the
// Latin runs LTR; in an LTR (English) story pin the Arabic/Hebrew runs RTL.
export function isolateBidi(str, dir) {
  if (str == null || str === '') return str;
  return dir === 'ltr' ? isolateRtlRuns(str) : isolateLtrRuns(str);
}

// Base text direction for a story from its personalization. English reads LTR —
// whether it was hand-typed (textLanguage='en', the manual builder) or generated
// (storyLanguage='en', the admin "create preview for a customer" flow). Anything
// else (Arabic/Hebrew) keeps the RTL base.
export function storyTextDir(personalization) {
  const p = personalization || {};
  return p.textLanguage === 'en' || p.storyLanguage === 'en' ? 'ltr' : 'rtl';
}
